import React from 'react';
import styled from 'styled-components';
import Footer from '../../Footer/Footer';

const PrivacyContainer = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 4rem 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
    margin: 2rem 1rem;
  }
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  text-align: center;
`;

const LastUpdated = styled.p`
  text-align: center;
  font-weight: 600;
  margin-bottom: 2.5rem;
  color: ${props => props.theme.colors.text};
`;

const Section = styled.section`
  margin-bottom: 2rem;

  h2 {
    color: ${props => props.theme.colors.primary};
    font-size: 1.8rem;
    margin-bottom: 1rem;
  }

  h3 {
    color: ${props => props.theme.colors.primary};
    font-size: 1.25rem;
    margin: 1.5rem 0 0.75rem;
  }

  p {
    line-height: 1.6;
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.text};
  }

  ul {
    margin-left: 2rem;
    margin-bottom: 1rem;

    li {
      line-height: 1.6;
      margin-bottom: 0.5rem;
      color: ${props => props.theme.colors.text};
    }
  }

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
  }

  address {
    font-style: normal;
    line-height: 1.6;
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.text};
  }
`;

const Privacy = () => {
  return (
    <PrivacyContainer>
      <ContentContainer>
        <Title>Privacy Policy</Title>
        <LastUpdated>Last updated: October 2026</LastUpdated>

        <Section>
          <h2>1. Introduction</h2>
          <p>
            At Belvoir Castle, we take the privacy and security of your personal information seriously.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, store and share personal information when you visit our website, make a booking or purchase, contact us, sign up to receive communications from us, or otherwise interact with Belvoir Castle.
          </p>
          <p>
            Belvoir Castle is responsible for determining how and why your personal information is used.
          </p>
        </Section>

        <Section>
          <h2>2. Information We Collect</h2>
          <p>
            Depending on how you interact with us, we may collect the following information:
          </p>

          <h3>Personal and Contact Information</h3>
          <ul>
            <li>Name</li>
            <li>Email address</li>
            <li>Telephone number</li>
            <li>Postal address</li>
            <li>Information you provide when contacting us</li>
          </ul>

          <h3>Booking and Transaction Information</h3>
          <ul>
            <li>Booking and reservation details</li>
            <li>Tickets, memberships and products purchased</li>
            <li>Transaction information</li>
            <li>Visit dates and booking history</li>
            <li>Payment-related information</li>
          </ul>
          <p>
            Payments may be processed on our behalf by third-party payment providers.
          </p>

          <h3>Marketing Information</h3>
          <ul>
            <li>Your marketing preferences</li>
            <li>Your interactions with our marketing communications</li>
            <li>Information about promotions or campaigns through which you reached us</li>
          </ul>

          <h3>Website and Technical Information</h3>
          <p>When you use our website, we may collect information such as:</p>
          <ul>
            <li>IP address</li>
            <li>Browser type</li>
            <li>Device information</li>
            <li>Pages visited</li>
            <li>Time spent on our website</li>
            <li>Website interactions</li>
            <li>Referring websites or advertising campaigns</li>
            <li>Cookie and analytics information</li>
          </ul>
          <p>
            More information about the technologies used on our website may also be provided through our Cookie Policy or cookie management tools.
          </p>
        </Section>

        <Section>
          <h2>3. How We Use Your Information</h2>
          <p>We may use your personal information to:</p>
          <ul>
            <li>Process bookings, ticket purchases, memberships and reservations</li>
            <li>Process payments and refunds</li>
            <li>Send booking confirmations and important information relating to your visit</li>
            <li>Respond to enquiries and provide customer support</li>
            <li>Administer memberships and customer accounts</li>
            <li>Improve our website, booking systems and visitor experience</li>
            <li>Protect our systems and prevent fraud or misuse</li>
            <li>Understand how our website and services are being used</li>
            <li>Measure the effectiveness and return on investment of our advertising and marketing campaigns</li>
            <li>Send marketing communications where you have consented or where otherwise permitted by law</li>
            <li>Meet our legal, accounting and regulatory obligations</li>
          </ul>
        </Section>

        <Section>
          <h2>4. Our Lawful Bases for Using Your Information</h2>
          <p>
            Under UK data protection law, we must have a lawful basis for processing personal information.
          </p>
          <p>Depending on the circumstances, we may rely on:</p>

          <h3>Contract</h3>
          <p>
            Where processing your information is necessary to provide something you have purchased or requested from us, such as processing a ticket booking, membership, reservation, payment or refund.
          </p>

          <h3>Legal Obligation</h3>
          <p>
            Where we are required to process or retain information to comply with legal, tax, accounting or regulatory requirements.
          </p>

          <h3>Legitimate Interests</h3>
          <p>
            Where processing is necessary for our legitimate business interests and those interests do not override your data protection rights.
          </p>
          <p>Our legitimate interests may include:</p>
          <ul>
            <li>Operating and improving Belvoir Castle and our services</li>
            <li>Understanding customer behaviour</li>
            <li>Improving our website and booking experience</li>
            <li>Maintaining the security of our systems</li>
            <li>Preventing fraud</li>
            <li>Analysing business and visitor performance</li>
            <li>Measuring the effectiveness and return on investment of our advertising campaigns</li>
          </ul>

          <h3>Consent</h3>
          <p>
            Where required, we will ask for your consent, for example for certain marketing communications or cookies.
          </p>
          <p>
            Where we rely on consent, you can withdraw it at any time.
          </p>
        </Section>

        <Section>
          <h2>5. Advertising Measurement and Campaign ROI</h2>
          <p>
            We invest in advertising to promote Belvoir Castle and may work with trusted advertising and measurement partners to understand whether our campaigns have resulted in bookings, visits or purchases.
          </p>
          <p>
            This may include partners such as <strong>Sky</strong> in connection with television and advertising campaigns.
          </p>
          <p>
            For these purposes, we may securely provide limited customer and transaction information required to carry out campaign measurement. This can allow our advertising partners to determine whether households exposed to a particular advertising campaign subsequently made a booking or purchase with Belvoir Castle.
          </p>
          <p>
            Where appropriate, information used for this matching process may be pseudonymised, hashed or otherwise protected before or during the matching process.
          </p>
          <p>
            This information is provided solely for the agreed campaign measurement and ROI analysis.
          </p>
          <p>
            Where we enter into these arrangements, formal and legally binding agreements govern how the information is handled. These agreements establish matters including:
          </p>
          <ul>
            <li>The specific purpose for which the information may be used</li>
            <li>What information may be processed</li>
            <li>Appropriate security and confidentiality requirements</li>
            <li>Who is permitted to access the information</li>
            <li>How long the information may be retained</li>
            <li>Requirements for deletion or destruction of the information</li>
            <li>Restrictions preventing the information from being repurposed for unrelated activities</li>
          </ul>
          <p>
            Information supplied for these measurement activities may not be sold or used by the relevant partner for unrelated marketing or its own independent commercial purposes where this is prohibited by our agreement with them.
          </p>
          <p>
            Data used for campaign matching is retained only for the period required to complete the agreed measurement and reporting process and is then deleted or destroyed in accordance with the applicable agreement.
          </p>
          <p>
            We use the results of this process to understand the overall effectiveness of our advertising and the return generated from our marketing expenditure.
          </p>
        </Section>

        <Section>
          <h2>6. Marketing Communications</h2>
          <p>
            We may contact you about Belvoir Castle news, events, offers and other relevant information where you have asked to receive these communications or where we are otherwise permitted to do so by law.
          </p>
          <p>
            You can unsubscribe from marketing emails at any time by using the unsubscribe link contained within the email or by contacting us.
          </p>
          <p>
            Opting out of marketing will not prevent us from sending necessary service communications relating to an existing booking, purchase or membership.
          </p>
        </Section>

        <Section>
          <h2>7. Sharing Your Information</h2>
          <p>
            We do not sell or rent your personal information.
          </p>
          <p>
            Where necessary, we may share information with trusted organisations that help us operate our business and provide our services, including:
          </p>
          <ul>
            <li>Ticketing and reservation providers</li>
            <li>Payment processors</li>
            <li>Website and IT service providers</li>
            <li>Hosting and security providers</li>
            <li>Email and communication providers</li>
            <li>Analytics providers</li>
            <li>Advertising and campaign measurement partners, including where appropriate Sky</li>
            <li>Professional advisers such as accountants, auditors or legal advisers</li>
            <li>Government bodies, regulators, law enforcement agencies or other authorities where required by law</li>
          </ul>
          <p>
            We only share information where there is an appropriate reason for doing so and require organisations processing information on our behalf to protect it appropriately.
          </p>
        </Section>

        <Section>
          <h2>8. International Transfers</h2>
          <p>
            Some of the organisations that provide services to us may process personal information outside the United Kingdom.
          </p>
          <p>
            Where personal information is transferred internationally, we take appropriate steps to ensure that the information continues to receive an appropriate level of protection in accordance with UK data protection law.
          </p>
          <p>
            Where required, this may include using approved contractual safeguards or relying on applicable UK adequacy regulations.
          </p>
          <p>
            You can contact us if you would like further information about the safeguards applying to a particular transfer.
          </p>
        </Section>

        <Section>
          <h2>9. How Long We Keep Your Information</h2>
          <p>
            We only retain personal information for as long as reasonably necessary for the purpose for which it was collected.
          </p>
          <p>The period for which information is retained will depend on factors including:</p>
          <ul>
            <li>The reason the information was collected</li>
            <li>Whether you continue to have a relationship with us</li>
            <li>Legal and accounting requirements</li>
            <li>The need to resolve disputes or complaints</li>
            <li>Fraud prevention and security requirements</li>
          </ul>
          <p>
            Information provided specifically for advertising measurement or data-matching exercises will only be retained for the period required to complete the agreed matching and reporting process and will then be deleted or destroyed in accordance with the applicable agreement.
          </p>
        </Section>

        <Section>
          <h2>10. Data Security</h2>
          <p>
            We use appropriate technical and organisational measures to protect personal information from unauthorised access, disclosure, alteration, loss or destruction.
          </p>
          <p>
            Access to personal information is restricted to people and organisations that require it for an authorised purpose.
          </p>
          <p>
            Where information is shared with third-party service providers or measurement partners, appropriate contractual and security controls are used where required.
          </p>
        </Section>

        <Section>
          <h2>11. Your Data Protection Rights</h2>
          <p>
            Depending on the circumstances and the lawful basis on which we process your information, you may have the right to:
          </p>
          <ul>
            <li>Request access to the personal information we hold about you</li>
            <li>Ask us to correct inaccurate or incomplete information</li>
            <li>Ask us to delete your personal information in certain circumstances</li>
            <li>Ask us to restrict how your information is processed</li>
            <li>Object to certain types of processing</li>
            <li>Request the transfer of certain information to you or another organisation</li>
            <li>Withdraw consent where processing is based on your consent</li>
          </ul>
          <p>
            These rights are not absolute and may depend on the circumstances in which your information is being processed.
          </p>

          <h3>Your Right to Object</h3>
          <p>
            <strong>You have the right to object to processing based on legitimate interests in certain circumstances.</strong>
          </p>
          <p>
            You also have the right to object at any time to the use of your personal information for direct marketing.
          </p>
          <p>
            To exercise any of your rights, please contact us using the details below.
          </p>
        </Section>

        <Section>
          <h2>12. Complaints</h2>
          <p>
            If you have concerns about how we have used your personal information, please contact us first so that we can investigate the matter.
          </p>
          <p>
            You also have the right to make a complaint to the UK supervisory authority for data protection:
          </p>
          <p>
            <strong>Information Commissioner&apos;s Office (ICO)</strong><br />
            Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>
          </p>
        </Section>

        <Section>
          <h2>13. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes to our services, systems, business activities or legal obligations.
          </p>
          <p>
            Where we make a significant change to the way we use personal information, we will take appropriate steps to bring the change to the attention of affected individuals where required.
          </p>
          <p>
            The date at the top of this policy shows when it was last updated.
          </p>
        </Section>

        <Section>
          <h2>14. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy, how we use your personal information, or would like to exercise your data protection rights, please contact:
          </p>
          <address>
            <strong>Belvoir Castle</strong><br />
            Belvoir Castle<br />
            Grantham<br />
            Leicestershire<br />
            NG32 1PE
          </address>
          <p>
            <strong>Email:</strong>{' '}
            <a href="mailto:info@belvoircastle.com">info@belvoircastle.com</a>
          </p>
        </Section>
      </ContentContainer>
      <Footer />
    </PrivacyContainer>
  );
};

export default Privacy;
